// Copyright (c) StrangeBee 2023

import React, { useEffect, useMemo, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import lodash from 'lodash';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useDrawer } from '@Hooks/useDrawer';
import AddItemButton from '@Atoms/Buttons/AddItemButton/AddItemButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';

import { ProviderDrawerProps } from './ProviderDrawerTypes';
import { SAMLManage } from './SAMLManage/SAMLManage';
import { SAMLInput } from './SAMLManage/SAMLForm';

import { ICONS } from '@Constants/iconsApp';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';

import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/mode-jsx';
import 'ace-builds/src-noconflict/mode-xml';

import './ProviderDrawer.scss';

const resetFields = {
    clients: [],
};

const SAMLDrawer = ({
    type,
    title = type,
    enableList,
    getData,
    onSubmit,
    triggerElem,
    availableProviders,
    className,
}: ProviderDrawerProps): ReactElement => {
    const local = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: true,
    });

    const dataCurrent = useMemo(() => getData(type), [getData, type]);
    const notAvailable = !availableProviders?.includes(type);

    const [initialValues, setInitialeValues] = useState<Record<string, any>>({});
    const [showForm, setShowForm] = useState<boolean>(false);

    const { visible, setVisible } = useDrawer<any>({});

    const [disabledSwitch, setDisabledSwitch] = useState(true);

    const [forbiddenNames, setForbiddenNames] = useState<string[]>([]);

    const computeOnFinishHandler = (toggleDrawer: any) => (state: any) => {
        const result = { ...dataCurrent, ...state, ...{ UIType: type } };
        onResetForm();
        toggleDrawer();
        onSubmit(result);
    };

    const onChangeSwitch = (value: any): void => {
        setShowForm(value);
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        initForm(enableList);
        form.resetFields();
    };

    const initForm = (enableList: string[]) => {
        const enabled = enableList.includes(type);
        setShowForm(enabled);
    };

    useEffect(() => {
        const enabled = enableList.includes(type);
        setShowForm(enabled);
        setInitialeValues({
            ...resetFields,
            ...lodash.cloneDeep(dataCurrent),
            ...{ UIType: type, UIenabled: enabled },
        });
    }, [dataCurrent, enableList]); //eslint-disable-line

    useEffect(() => {
        initForm(enableList);
        setDisabledSwitch(!enableList.some((v) => ['local', 'ad', 'ldap', 'oauth2'].includes(v)));
        // eslint-disable-next-line
    }, [enableList]);

    useEffect(() => {
        if (dataCurrent?.clients?.length) {
            setForbiddenNames(dataCurrent?.clients?.map(({ name }: SAMLInput) => name));
        }
    }, [dataCurrent]);

    useEffect(() => {
        form.resetFields();
    }, [initialValues]); //eslint-disable-line

    return (
        <>
            <THDrawer
                title={title}
                className={classList('auth-details__providers-drawer', className)}
                triggerElem={triggerElem}
                disableClose={fieldsTouched}
                afterVisibleChange={() => {
                    initForm(enableList);
                    form.resetFields();
                }}
            >
                {(toggleDrawer) => (
                    <THForm
                        onFinish={computeOnFinishHandler(toggleDrawer)}
                        onValuesChange={() => {
                            setFieldsTouched(true);
                            onFormValuesChange();
                        }}
                        form={form}
                        initialValues={initialValues}
                    >
                        <THForm.Layout
                            footer={
                                <FormSubmitButton
                                    disabled={!isValid}
                                    onCancel={() => {
                                        onResetForm();
                                        toggleDrawer();
                                    }}
                                    hidden={!fieldsTouched || notAvailable}
                                />
                            }
                        >
                            <THForm.Item
                                name={'UIenabled'}
                                label={local('configPlateform.saml.enabled')}
                                valuePropName="checked"
                            >
                                <THSwitch onChange={onChangeSwitch} disabled={disabledSwitch || notAvailable} />
                            </THForm.Item>
                            {showForm && (
                                <THForm.List name="clients">
                                    {(fields, { remove }) => {
                                        return (
                                            <>
                                                <TitleSection
                                                    className="saml-manage-form__title-section"
                                                    extraAction={<AddItemButton onClick={() => setVisible(true)} />}
                                                >
                                                    {local('configPlateform.saml.provider.label')}
                                                </TitleSection>
                                                {!fields.length && (
                                                    <DisplayEmptyData
                                                        label={local('configPlateform.saml.provider.none')}
                                                    >
                                                        <THButton type="link" onClick={() => setVisible(true)}>
                                                            {local('configPlateform.saml.provider.add')}
                                                        </THButton>
                                                    </DisplayEmptyData>
                                                )}
                                                {fields.length ? (
                                                    <div className="saml-list">
                                                        {fields.map((field, index) => {
                                                            const clientCurrentSAML = form.getFieldValue('clients')?.[
                                                                field.name
                                                            ] as SAMLInput | undefined;

                                                            return (
                                                                <THForm.Item key={field.key} noStyle>
                                                                    <div className="saml-list-item">
                                                                        <div className="saml-list-item__title">
                                                                            <SAMLManage
                                                                                forbiddenNames={forbiddenNames}
                                                                                onFinish={(value: any) => {
                                                                                    setForbiddenNames((list) => [
                                                                                        ...list,
                                                                                        value.name,
                                                                                    ]);
                                                                                    form.setFieldsValue({
                                                                                        clients: (
                                                                                            form.getFieldValue(
                                                                                                'clients',
                                                                                            ) || []
                                                                                        ).map((item: SAMLInput) =>
                                                                                            item.name === value.name
                                                                                                ? value
                                                                                                : item,
                                                                                        ),
                                                                                    });
                                                                                    setFieldsTouched(true);
                                                                                    return Promise.resolve(value);
                                                                                }}
                                                                                triggerElem={
                                                                                    <THButton type="link">
                                                                                        {clientCurrentSAML?.displayName ||
                                                                                            clientCurrentSAML?.name}
                                                                                    </THButton>
                                                                                }
                                                                                defaultValues={clientCurrentSAML}
                                                                            />
                                                                        </div>

                                                                        <div className="saml-list-item__actions">
                                                                            <ActionsList
                                                                                actionList={{
                                                                                    delete: {
                                                                                        label: local('general.delete'),
                                                                                        confirm: true,
                                                                                        message: local(
                                                                                            'configPlateform.saml.provider.confirmDelete',
                                                                                        ),
                                                                                        query: () => {
                                                                                            remove(index);
                                                                                            setForbiddenNames(
                                                                                                form
                                                                                                    .getFieldValue(
                                                                                                        'clients',
                                                                                                    )
                                                                                                    .map(
                                                                                                        ({
                                                                                                            name,
                                                                                                        }: SAMLInput) =>
                                                                                                            name,
                                                                                                    ),
                                                                                            );
                                                                                        },
                                                                                        icon: <ICONS.DeleteIcon />,
                                                                                    },
                                                                                }}
                                                                                isIcon
                                                                                scope="single"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </THForm.Item>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </>
                                        );
                                    }}
                                </THForm.List>
                            )}
                        </THForm.Layout>
                    </THForm>
                )}
            </THDrawer>
            {visible && (
                <SAMLManage
                    forbiddenNames={forbiddenNames}
                    visible={visible}
                    onVisibleChange={setVisible}
                    onFinish={(value: any) => {
                        setForbiddenNames((list) => [...list, value.name]);
                        form.setFields([
                            {
                                name: 'clients',
                                value: [...(form.getFieldValue('clients') || []), value],
                            },
                        ]);
                        setFieldsTouched(true);
                        return Promise.resolve(value);
                    }}
                />
            )}
        </>
    );
};

export default SAMLDrawer;
